import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";   // ⚠️ même import que dans Documents.js
import "./DashboardArchives.css";

const baseUrl = process.env.REACT_APP_BASE_URL;
const API = {
    documents: `${baseUrl}/api/ged/documents`,
    trash: `${baseUrl}/api/ged/documents/trash`,
    folders: `${baseUrl}/api/ged/folders`,
    ventes: `${baseUrl}/api/CommercialDocumentVentes`,
    achats: `${baseUrl}/api/CommercialDocumentAchats`,
    projets: `${baseUrl}/api/DocumentProjets`,
    rh: `${baseUrl}/api/DocumentEmployes`,
};
// ⚠️ Route de la page Documents dans Content.js
const ROUTE_DOCUMENTS = "/archives/documents";

const CATEGORIES = [
    { value: 1, name: "Vente", label: "Vente", color: "#1f9d55", icon: "fa-file-invoice-dollar" },
    { value: 2, name: "Achat", label: "Achat", color: "#2a78d6", icon: "fa-shopping-cart" },
    { value: 3, name: "RH", label: "Ressources humaines", color: "#17a2b8", icon: "fa-user-tie" },
    { value: 4, name: "Projet", label: "Projet", color: "#eb6834", icon: "fa-hard-hat" },
];
const NON_CLASSE = { value: 0, label: "Non classé", color: "#adb5bd", icon: "fa-question-circle" };
const findCat = (c) => CATEGORIES.find((x) => x.value === Number(c) || x.name === c) || null;

const FILE_KINDS = [
    { key: "pdf", label: "PDF", icon: "fa-file-pdf", color: "#d64545", ext: ["pdf"] },
    { key: "image", label: "Images", icon: "fa-file-image", color: "#2a78d6", ext: ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff"] },
    { key: "word", label: "Word", icon: "fa-file-word", color: "#3b5bdb", ext: ["doc", "docx", "odt", "rtf"] },
    { key: "excel", label: "Excel", icon: "fa-file-excel", color: "#1f9d55", ext: ["xls", "xlsx", "csv", "ods"] },
    { key: "video", label: "Vidéos", icon: "fa-file-video", color: "#7048e8", ext: ["mp4", "mov", "avi", "mkv", "webm"] },
    { key: "autre", label: "Autres", icon: "fa-file", color: "#868e96", ext: [] },
];
const kindOf = (d) => {
    const ext = (d.fileName || "").split(".").pop().toLowerCase();
    const ct = (d.contentType || "").toLowerCase();
    return FILE_KINDS.find((k) => k.ext.includes(ext))
        || (ct.startsWith("image/") ? FILE_KINDS[1] : ct.startsWith("video/") ? FILE_KINDS[4] : ct.includes("pdf") ? FILE_KINDS[0] : FILE_KINDS[5]);
};

const isArchived = (d) => d.status === 2 || d.status === "Archived";
const isDeleted = (d) => d.status === 3 || d.status === "Deleted";
const unwrap = (res) => (res?.data && res.data.data !== undefined ? res.data.data : res?.data);
const list = (res) => { const v = unwrap(res); return Array.isArray(v) ? v : Array.isArray(v?.$values) ? v.$values : []; };

const formatSize = (bytes) => {
    if (!bytes) return "0 o";
    const u = ["o", "Ko", "Mo", "Go", "To"];
    let s = Number(bytes), i = 0;
    while (s >= 1024 && i < u.length - 1) { s /= 1024; i++; }
    return `${s.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "—");
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

const DashboardArchives = () => {
    const [loading, setLoading] = useState(true);
    const [docs, setDocs] = useState([]);
    const [trash, setTrash] = useState([]);
    const [folders, setFolders] = useState([]);
    const [links, setLinks] = useState({ ventes: [], achats: [], projets: [], rh: [] });

    useEffect(() => {
        const get = (url) => axiosInstance.get(url).catch(() => null);
        Promise.all([get(API.documents), get(API.trash), get(API.folders), get(API.ventes), get(API.achats), get(API.projets), get(API.rh)])
            .then(([d, t, f, v, a, p, r]) => {
                setDocs(list(d).map((x) => ({ ...x, category: x.category ?? x.Category ?? x.documentCategory ?? null, createdAt: x.createdAt ?? x.uploadedAt ?? null })));
                setTrash(list(t));
                setFolders(list(f));
                setLinks({ ventes: list(v), achats: list(a), projets: list(p), rh: list(r) });
            })
            .finally(() => setLoading(false));
    }, []);

    const stats = useMemo(() => {
        const live = docs.filter((d) => !isDeleted(d));
        const ids = (arr) => new Set(arr.map((x) => String(x.id)));
        const venteIds = ids(links.ventes), achatIds = ids(links.achats), projetIds = ids(links.projets), rhIds = ids(links.rh);

        // Catégorie effective : renvoyée par l'API, sinon déduite du rattachement
        const catOf = (d) => findCat(d.category)
            || (venteIds.has(String(d.id)) ? CATEGORIES[0] : achatIds.has(String(d.id)) ? CATEGORIES[1]
                : rhIds.has(String(d.id)) ? CATEGORIES[2] : projetIds.has(String(d.id)) ? CATEGORIES[3] : null);
        const hasInfos = (d) => [venteIds, achatIds, projetIds, rhIds].some((s) => s.has(String(d.id)));

        const now = new Date();
        const debutMois = new Date(now.getFullYear(), now.getMonth(), 1);
        const debutMoisPrec = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const dateOf = (d) => (d.createdAt ? new Date(d.createdAt) : null);

        // 12 derniers mois
        const mois = Array.from({ length: 12 }, (_, i) => {
            const dt = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
            return { key: `${dt.getFullYear()}-${dt.getMonth()}`, label: MOIS_COURTS[dt.getMonth()], annee: dt.getFullYear(), n: 0 };
        });
        const moisIdx = Object.fromEntries(mois.map((m, i) => [m.key, i]));
        live.forEach((d) => {
            const dt = dateOf(d); if (!dt) return;
            const i = moisIdx[`${dt.getFullYear()}-${dt.getMonth()}`];
            if (i !== undefined) mois[i].n++;
        });

        // Catégories
        const parCat = [...CATEGORIES, NON_CLASSE].map((c) => ({ ...c, n: 0, size: 0 }));
        live.forEach((d) => {
            const c = catOf(d);
            const row = parCat.find((x) => x.value === (c ? c.value : 0));
            row.n++; row.size += Number(d.size ?? d.fileSize ?? 0);
        });

        // Types de fichiers
        const parKind = FILE_KINDS.map((k) => ({ ...k, n: 0 }));
        live.forEach((d) => { parKind.find((k) => k.key === kindOf(d).key).n++; });

        // Dossiers (chemin complet)
        const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
        const path = (id) => { const p = []; let c = byId[id], g = 0; while (c && g++ < 20) { p.unshift(c.name); c = byId[c.parentId]; } return p.join(" / "); };
        const cptFolder = {};
        live.forEach((d) => { const k = d.folderId || "__root__"; cptFolder[k] = (cptFolder[k] || 0) + 1; });
        const topFolders = Object.entries(cptFolder)
            .map(([id, n]) => ({ id, n, nom: id === "__root__" ? "(Racine)" : path(id) || "Dossier supprimé" }))
            .sort((a, b) => b.n - a.n).slice(0, 8);

        // RH : expirés / bientôt expirés (30 jours)
        const dans30 = new Date(now.getTime() + 30 * 86400000);
        const rhAlertes = links.rh
            .filter((r) => r.dateExpiration && new Date(r.dateExpiration) <= dans30)
            .map((r) => ({ ...r, expire: new Date(r.dateExpiration) < now }))
            .sort((a, b) => new Date(a.dateExpiration) - new Date(b.dateExpiration));

        return {
            total: live.length,
            actifs: live.filter((d) => !isArchived(d)).length,
            archives: live.filter(isArchived).length,
            taille: live.reduce((s, d) => s + Number(d.size ?? d.fileSize ?? 0), 0),
            ceMois: live.filter((d) => dateOf(d) >= debutMois).length,
            moisPrec: live.filter((d) => { const dt = dateOf(d); return dt >= debutMoisPrec && dt < debutMois; }).length,
            nonClasses: live.filter((d) => !catOf(d)).length,
            aCompleter: live.filter((d) => findCat(d.category) && !hasInfos(d)).length,
            corbeille: trash.length || docs.filter(isDeleted).length,
            dossiers: folders.length,
            mois, parCat, parKind, topFolders, rhAlertes,
            recents: [...live].filter(dateOf).sort((a, b) => dateOf(b) - dateOf(a)).slice(0, 8).map((d) => ({ ...d, cat: catOf(d) })),
        };
    }, [docs, trash, folders, links]);

    if (loading) return <div className="container-fluid mt-4"><p><i className="fas fa-spinner fa-spin mr-2"></i>Chargement du tableau de bord…</p></div>;

    const maxMois = Math.max(1, ...stats.mois.map((m) => m.n));
    const maxCat = Math.max(1, ...stats.parCat.map((c) => c.n));
    const maxKind = Math.max(1, ...stats.parKind.map((k) => k.n));
    const maxFolder = Math.max(1, ...stats.topFolders.map((f) => f.n));
    const evol = stats.ceMois - stats.moisPrec;

    const Kpi = ({ label, value, sub, icon, tone }) => (
        <div className="col-6 col-md-4 col-xl-2 mb-3">
            <div className={`da-kpi da-kpi-${tone || "neutral"}`}>
                <div className="da-kpi-head"><i className={`fas ${icon} mr-1`}></i>{label}</div>
                <div className="da-kpi-value">{value}</div>
                {sub && <div className="da-kpi-sub">{sub}</div>}
            </div>
        </div>
    );

    return (
        <div className="container-fluid mt-4 da">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0"><i className="fas fa-archive mr-2"></i>Tableau de bord des archives</h3>
                <Link to={ROUTE_DOCUMENTS} className="btn btn-primary btn-sm"><i className="fas fa-folder-open mr-1"></i>Ouvrir les documents</Link>
            </div>

            <div className="row">
                <Kpi label="Documents" value={stats.total} sub={`${stats.actifs} actifs • ${stats.archives} archivés`} icon="fa-file-alt" />
                <Kpi label="Espace occupé" value={formatSize(stats.taille)} sub={`${stats.dossiers} dossiers`} icon="fa-hdd" />
                <Kpi label="Ajoutés ce mois" value={stats.ceMois}
                    sub={`${evol >= 0 ? "+" : ""}${evol} par rapport au mois dernier`} icon="fa-upload" tone={evol >= 0 ? "good" : "neutral"} />
                <Kpi label="Non classés" value={stats.nonClasses} sub="sans catégorie" icon="fa-question-circle" tone={stats.nonClasses ? "warning" : "good"} />
                <Kpi label="À compléter" value={stats.aCompleter} sub="catégorie sans informations" icon="fa-edit" tone={stats.aCompleter ? "warning" : "good"} />
                <Kpi label="Corbeille" value={stats.corbeille} sub="en attente de suppression" icon="fa-trash-alt" tone={stats.corbeille ? "critical" : "neutral"} />
            </div>

            <div className="row">
                <div className="col-lg-8 mb-3">
                    <div className="card h-100"><div className="card-header">Documents ajoutés — 12 derniers mois</div>
                        <div className="card-body">
                            <div className="da-cols">
                                {stats.mois.map((m) => (
                                    <div key={m.key} className="da-col" title={`${m.label} ${m.annee} : ${m.n} document(s)`}>
                                        <span className="da-col-val">{m.n || ""}</span>
                                        <div className="da-col-bar" style={{ height: `${(m.n / maxMois) * 100}%` }}></div>
                                        <span className="da-col-lbl">{m.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 mb-3">
                    <div className="card h-100"><div className="card-header">Par catégorie</div>
                        <div className="card-body">
                            {stats.parCat.map((c) => (
                                <div key={c.value} className="da-hbar">
                                    <div className="da-hbar-lbl"><i className={`fas ${c.icon} mr-1`} style={{ color: c.color }}></i>{c.label}</div>
                                    <div className="da-hbar-track"><div className="da-hbar-fill" style={{ width: `${(c.n / maxCat) * 100}%`, background: c.color }}></div></div>
                                    <div className="da-hbar-val">{c.n}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-4 mb-3">
                    <div className="card h-100"><div className="card-header">Par type de fichier</div>
                        <div className="card-body">
                            {stats.parKind.filter((k) => k.n > 0 || k.key === "pdf").map((k) => (
                                <div key={k.key} className="da-hbar">
                                    <div className="da-hbar-lbl"><i className={`fas ${k.icon} mr-1`} style={{ color: k.color }}></i>{k.label}</div>
                                    <div className="da-hbar-track"><div className="da-hbar-fill" style={{ width: `${(k.n / maxKind) * 100}%`, background: k.color }}></div></div>
                                    <div className="da-hbar-val">{k.n}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 mb-3">
                    <div className="card h-100"><div className="card-header">Dossiers les plus remplis</div>
                        <div className="card-body">
                            {stats.topFolders.length === 0 && <p className="text-muted mb-0">Aucun document.</p>}
                            {stats.topFolders.map((f) => (
                                <div key={f.id} className="da-hbar">
                                    <div className="da-hbar-lbl text-truncate" title={f.nom}><i className="fas fa-folder text-warning mr-1"></i>{f.nom}</div>
                                    <div className="da-hbar-track"><div className="da-hbar-fill" style={{ width: `${(f.n / maxFolder) * 100}%` }}></div></div>
                                    <div className="da-hbar-val">{f.n}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 mb-3">
                    <div className="card h-100"><div className="card-header d-flex justify-content-between">
                        <span>Documents RH à renouveler</span>
                        {stats.rhAlertes.length > 0 && <span className="badge badge-danger">{stats.rhAlertes.length}</span>}
                    </div>
                        <div className="card-body p-0">
                            {stats.rhAlertes.length === 0 && <p className="text-muted m-3">Aucun document expiré ni à échéance sous 30 jours.</p>}
                            <ul className="list-group list-group-flush">
                                {stats.rhAlertes.slice(0, 8).map((r) => (
                                    <li key={r.id} className="list-group-item py-2 d-flex justify-content-between align-items-center">
                                        <span className="text-truncate mr-2">
                                            <strong>{r.employeNom}</strong>
                                            <small className="d-block text-muted text-truncate">{r.intitule || r.fileName}</small>
                                        </span>
                                        <span className={`badge ${r.expire ? "badge-danger" : "badge-warning"}`}>
                                            {r.expire ? "Expiré" : "Expire"} {fmtDate(r.dateExpiration)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mb-4"><div className="card-header">Derniers documents ajoutés</div>
                <div className="card-body p-0">
                    <table className="table table-sm table-hover mb-0">
                        <thead><tr><th>Fichier</th><th>Catégorie</th><th>Taille</th><th>Ajouté le</th></tr></thead>
                        <tbody>
                            {stats.recents.length === 0 && <tr><td colSpan="4" className="text-center text-muted py-3">Aucun document</td></tr>}
                            {stats.recents.map((d) => {
                                const k = kindOf(d);
                                return (
                                    <tr key={d.id}>
                                        <td className="text-truncate" style={{ maxWidth: 340 }}><i className={`fas ${k.icon} mr-2`} style={{ color: k.color }}></i>{d.fileName}</td>
                                        <td>{d.cat ? <span className="badge" style={{ background: d.cat.color, color: "#fff" }}>{d.cat.label}</span> : <span className="text-muted">Non classé</span>}</td>
                                        <td>{formatSize(d.size ?? d.fileSize)}</td>
                                        <td>{fmtDate(d.createdAt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DashboardArchives;
